import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import PostCard from '../components/posts/PostCard';
import SEOHead from '../components/seo/SEOHead';
import StructuredData from '../components/seo/StructuredData';
import { getFullUrl } from '../utils/urlUtils';

/**
 * Formatea una fecha para su uso en datos estructurados, manejando diferentes tipos de fecha
 * @param dateValue Valor de fecha que puede ser Timestamp, Date, string, número o null
 * @returns Fecha en formato ISO string
 */
const formatDateForStructuredData = (dateValue: Timestamp | Date | string | number | null): string => {
  try {
    // Si es null o undefined, usar la fecha actual
    if (!dateValue) {
      return new Date().toISOString();
    }

    // Si es un Timestamp de Firestore
    if (dateValue instanceof Timestamp) {
      return new Date(dateValue.toMillis()).toISOString();
    }

    // Si ya es un objeto Date
    if (dateValue instanceof Date) {
      return dateValue.toISOString();
    }

    // Si es un número (timestamp en milisegundos)
    if (typeof dateValue === 'number') {
      return new Date(dateValue).toISOString();
    }

    // Si es un string, intentar convertirlo a Date
    if (typeof dateValue === 'string') {
      // Verificar si es un objeto serializado
      if (dateValue.includes('seconds') && dateValue.includes('nanoseconds')) {
        try {
          // Intentar parsear como objeto Timestamp serializado
          const parsedObj = JSON.parse(dateValue);
          if (parsedObj.seconds) {
            return new Date(parsedObj.seconds * 1000).toISOString();
          }
        } catch (e) {
          // Si falla el parsing, continuar con el siguiente método
        }
      }

      // Intentar crear una fecha directamente
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        return date.toISOString();
      }
    }

    // Si todo falla, devolver la fecha actual
    return new Date().toISOString();
  } catch (error) {
    console.error('Error al formatear fecha para datos estructurados:', error);
    return new Date().toISOString(); // Valor seguro por defecto
  }
};

interface Post {
  id: string;
  userId: string;
  userName: string;
  userPhotoURL: string;
  content: string;
  imageUrl?: string;
  videoUrl?: string;
  likes: number;
  comments: number;
  createdAt: Timestamp | Date | null;
}

const PostPage: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();

  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPost = async () => {
      if (!postId) {
        setError('ID de publicación no válido');
        setLoading(false);
        return;
      }

      try {
        const postRef = doc(db, 'posts', postId);
        const postSnap = await getDoc(postRef);

        if (postSnap.exists()) {
          const postData = postSnap.data() as Post;
          setPost({
            ...postData,
            id: postSnap.id
          });
        } else {
          setError('La publicación no existe o ha sido eliminada');
        }
      } catch (err) {
        console.error('Error al cargar la publicación:', err);
        setError('Error al cargar la publicación. Inténtalo de nuevo más tarde.');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [postId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-domired-600"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="max-w-2xl mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md mt-8">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">Error</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">{error || 'No se pudo cargar la publicación'}</p>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-domired-600 text-white rounded-md hover:bg-domired-700 transition-colors"
        >
          Volver al inicio
        </button>
      </div>
    );
  }

  // Preparar datos para SEO
  const postUrl = getFullUrl(`/post/${post.id}`);
  const postImage = post.imageUrl || post.userPhotoURL || 'https://domired.top/logo.png';
  const postDescription = post.content.substring(0, 150) + (post.content.length > 150 ? '...' : '');

  return (
    <div className="max-w-2xl mx-auto">
      {/* SEO Metadata */}
      <SEOHead
        title={`${post.userName} en DomiRed: ${post.content.substring(0, 50)}${post.content.length > 50 ? '...' : ''}`}
        description={postDescription}
        image={postImage}
        url={postUrl}
        type="article"
      />

      {/* Structured Data for Article */}
      <StructuredData
        type="Article"
        data={{
          headline: `Publicación de ${post.userName}`,
          image: postImage,
          datePublished: formatDateForStructuredData(post.createdAt),
          author: {
            '@type': 'Person',
            name: post.userName
          }
        }}
      />

      <div className="mb-4">
        <h1 className="sr-only">Publicación de {post.userName}</h1>
        <PostCard post={post} />
      </div>
    </div>
  );
};

export default PostPage;
